/* global window navigator */
import { patch, post, del, put } from 'Server';
import {
    urls,
    operateFavouriteItem
} from 'Reducers/user';
import { UAParser } from 'ua-parser-js';
import { PLATFORM } from 'Constants/device.APP_TARGET';
import { getUrlNextPage } from 'Reducers/componentHelpers';
import {
    getUserDataSelector,
    isFetchingUserData,
    userAuthenticateData,
    getLoggedUser,
    getFollowersSelector,
    isFetchingFollowers,
    getFollowingSelector,
    isFetchingFollowing,
    favouriteItemSelector
} from 'Selectors/user';
import { getSelectedLangCode, getSiteCode } from 'Selectors/config';
import { getUserInfoByCategory } from 'Helpers/users';

const getLoggedUserData = (forceFetch = false) => (dispatch, getState, { get }) => {
    const state = getState();
    const user = getUserDataSelector(state);

    if ((user || isFetchingUserData(state)) && !forceFetch) {
        return Promise.resolve();
    }

    return dispatch(get(urls.getUserData, 'USERS'));
};
const getLoggedUserDealerData = (forceFetch = false) => (dispatch, getState, { get }) => {
    const state = getState();
    const user = getUserDataSelector(state);

    if ((user || isFetchingUserData(state)) && !forceFetch) {
        return Promise.resolve();
    }

    return dispatch(get(urls.getUserDealerData, 'USERS'));
};

const updateDealerDetails = params => (dispatch, getState) => {
    if (!params) {
        return Promise.reject();
    }
    const state = getState();
    const userId = getLoggedUser(state).id;

    return dispatch(put(urls.updateDealerData(userId), 'USERS', params));
};

const getFollowers = () => (dispatch, getState, { get }) => {
    const state = getState();
    const user = getLoggedUser(state);
    const followers = getFollowersSelector(state);

    if (followers.length || isFetchingFollowers(state)) {
        return Promise.resolve();
    }

    return dispatch(get(urls.getFollowers(user.id), 'USERS'));
};

const getDealerData = (type, id) => (dispatch, getState, { get }) => {
    const state = getState();
    const userId = id || getLoggedUser(state).id;

    if (type === 'showroom') {
        return dispatch(get(urls.getDealerData(userId, type), 'DEALERSDATA'));
    }
    return dispatch(get(urls.getDealerData(userId, type), 'DEALERS_PREVIEW_DATA'));
};

const postImage = params => dispatch => {
    return dispatch(post(urls.uploadImage(), 'DEALEARSDATAIMG', params));
};

const getNextFollowers = () => (dispatch, getState, { get }) => {
    const state = getState();
    const user = getLoggedUser(state);
    const lang = getSelectedLangCode(state);

    if (lang) {
        return dispatch(get(getUrlNextPage(state.users, urls.getFollowers(user.id), 'USERS', { lang }), 'USERS'));
    }

    return dispatch(get(getUrlNextPage(state.users, urls.getFollowers(user.id), 'USERS'), 'USERS'));
};

const getFollowing = () => (dispatch, getState, { get }) => {
    const state = getState();
    const user = getLoggedUser(state);
    const following = getFollowingSelector(state);

    if (following.length || isFetchingFollowing(state)) {
        return Promise.resolve();
    }

    return dispatch(get(urls.getFollowing(user.id), 'USERS'));
};

const getNextFollowing = () => (dispatch, getState, { get }) => {
    const state = getState();
    const user = getLoggedUser(state);
    const lang = getSelectedLangCode(state);

    if (lang) {
        return dispatch(get(getUrlNextPage(state.users, urls.getFollowing(user.id), 'USERS', { lang }), 'USERS'));
    }

    return dispatch(get(getUrlNextPage(state.users, urls.getFollowing(user.id), 'USERS'), 'USERS'));
};

const updateExistingUser = params => (dispatch, getState) => {
    if (!params) {
        return Promise.reject();
    }
    const state = getState();
    const userId = getLoggedUser(state).id;

    return dispatch(patch(urls.updateUser(userId), 'USERS', params));
};

const requestUserData = userId => dispatch => {
    if (!userId) {
        return Promise.reject();
    }

    return dispatch(post(urls.requestData(userId), 'USERS'));
};

const getUserFavouritesIds = () => (dispatch, getState, { get }) => {
    const state = getState();
    const user = getLoggedUser(state);
    const sitecode = getSiteCode(state);
    const params = {};
    const headers = { 'x-panamera-sitecode': sitecode };

    const { isFetching, isFetched } = getState().user;

    if (!isFetched && !isFetching && user && user.id) {
        return dispatch(get(urls.getFavouritesIds(user.id), 'FAVOURITES', params, headers));
    }
    return Promise.resolve();
};

const transformData = response => {
    if (response.ok) {
        return {
            data: response.data.data.map(item => {
                const dealer = getUserInfoByCategory(response.data.metadata?.users[item.user_id], item.category_id);

                return {
                    adId: parseInt(item.id, 10),
                    categoryId: item.category_id,
                    createdAt: item.created_at,
                    dealerType: dealer.dealer_type
                };
            })
        };
    }
    return null;
};

const recieveFavourites = response => ({
    type: 'RECEIVE_FAVOURITES',
    data: transformData(response)
});

const addUserFavourite = (userId, itemId, categoryId, dealerType) => (dispatch, getState) => {
    const state = getState();
    const sitecode = getSiteCode(state);
    const headers = { 'x-panamera-sitecode': sitecode };
    const params = { ads: [{ adId: itemId, categoryId, dealerType }] };

    if (!userId) {
        return Promise.reject();
    }
    if (favouriteItemSelector(state, itemId)) {
        return Promise.resolve();
    }
    return dispatch(post(`${urls.addFavourite(userId)}`, 'FAVOURITES', params, headers))
        .then(response => dispatch(operateFavouriteItem(response?.data?.data?.[0], 'ADD')));
};

const removeUserFavourite = (userId, itemId, categoryId, dealerType) => (dispatch, getState) => {
    const state = getState();
    const sitecode = getSiteCode(state);
    const params = {};
    const headers = { 'x-panamera-sitecode': sitecode };

    if (!userId) {
        return Promise.reject();
    }
    return dispatch(del(urls.removeFavourite(userId, itemId, categoryId, dealerType), 'FAVOURITES', params, headers))
        .then(() => dispatch(operateFavouriteItem(itemId, 'REMOVE')));
};

const linkAccount = (type, token, forceImage = false) => dispatch => {
    if (!type || !token) {
        return Promise.reject();
    }

    const params = {
        data: {
            type,
            token,
            force_image: forceImage
        }
    };

    return dispatch(post(urls.linkAccount, 'USERS', params));
};

const unLinkAccount = type => (dispatch, getState) => {
    if (!type) {
        return Promise.reject();
    }

    const state = getState();
    const params = {
        data: {
            type
        }
    };
    const userId = getLoggedUser(state).id;

    return dispatch(put(urls.unLinkAccount(userId), 'USERS', params));
};

const createChallenge = (type, descriptor) => dispatch => {
    if (!type || !descriptor) {
        return Promise.reject();
    }

    const params = {
        type,
        descriptor
    };

    return dispatch(post(urls.createChallenge, 'USERS', params));
};

const validateChallenge = (type, descriptor, secret) => dispatch => {
    if (!type || !descriptor || !secret) {
        return Promise.reject();
    }

    const params = {
        type,
        descriptor,
        secret
    };

    return dispatch(post(urls.validateChallenge, 'USERS', params));
};

const getUserLoginDetails = reduxState => {
    try {
        let loginDetails = {};

        if (navigator && navigator.userAgent) {
            const parser = new UAParser(navigator.userAgent);
            const result = parser.getResult();

            if (result) {
                loginDetails.model = result.device?.model || '';
                loginDetails.browser = result.browser?.name || '';
                const os = result.os?.name && result.os?.version
                    ? `${result.os.name} ${result.os.version}`
                    : '';

                loginDetails.platform = `Web-${PLATFORM}: ${os}`;
            }
        }

        const liveLocationArray = reduxState.locations.gps.location;

        if (liveLocationArray.length) {
            const cityName = liveLocationArray.filter(locationObj => locationObj.type === 'CITY');
            const state = liveLocationArray.filter(locationObj => locationObj.type === 'STATE');

            loginDetails.cityName = cityName.length ? cityName[0].name : undefined;
            loginDetails.state = state.length ? state[0].name : undefined;
            loginDetails.location = {
                lat: liveLocationArray[0].latitude,
                'long': liveLocationArray[0].longitude
            };
        }
        loginDetails = JSON.stringify(loginDetails);
        return window.btoa(loginDetails);
    }
    catch (err) {
        return null;
    }
};

// Legion
const authenticateLogin = (params, isV3 = false) => (dispatch, getState) => {
    const state = getState();
    const userData = userAuthenticateData(state);
    const headers = {};
    const loginDet = getUserLoginDetails(state);

    if (loginDet) {
        params.metadata = { deviceInfo: loginDet };
    }

    if (userData
        && userData.auth
        && userData.auth.token) {
        headers.Authorization = `Bearer ${userData.auth.token}`;
    }
    const url = isV3 ? urls.authenticateLoginV3 : urls.authenticateLogin;

    return dispatch(post(url, 'USER', params, headers));
};

const authenticate = (params, isV3 = false) => dispatch => {
    const headers = {};
    const url = isV3 ? urls.authenticateV3 : urls.authenticate;

    return dispatch(post(url, 'USER', params, headers));
};

const authenticateAutoLogin = params => dispatch => {
    const headers = params.headers ? params.headers : {};

    if (params.headers) {
        delete params.headers;
    }

    return dispatch(post(urls.authenticate, 'AUTOLOGIN', params, headers));
};

const deauthorize = () => dispatch => {
    const headers = {};

    return dispatch(del(urls.deauthorize, 'USER', headers));
};

const getUserConsents = (type, identifier) => dispatch => {
    return dispatch(post(urls.getUserConsents, '', { data: { type, identifier }}, {}));
};

const updateUserConsents = (id, params) => dispatch => {
    return dispatch(put(urls.updateUserConsents(id), '', { data: { consents: params }}, {}));
};

export {
    getLoggedUserData,
    getLoggedUserDealerData,
    updateExistingUser,
    deauthorize,
    recieveFavourites,
    requestUserData,
    addUserFavourite,
    removeUserFavourite,
    getUserFavouritesIds,
    linkAccount,
    unLinkAccount,
    createChallenge,
    validateChallenge,
    getFollowers,
    getNextFollowers,
    getFollowing,
    getNextFollowing,
    authenticateLogin,
    authenticate,
    authenticateAutoLogin,
    getUserConsents,
    updateUserConsents,
    getDealerData,
    updateDealerDetails,
    postImage
};
